<template>
  <div class="slow-drama-view" v-loading="isLoading">
    <el-button
      type="primary"
      class="modify-the-prompt"
      @click="dialogTableVisible = true"
      >修改提示词</el-button
    >
    <div class="scripts-grid">
      <div v-for="item in scripts" :key="item.id" class="script-card">
        <div class="card-body" @click="openScript(item)">
          <div class="card-icon">
            <el-icon>
              <Document />
            </el-icon>
          </div>
          <div class="card-title" :title="item.title">{{ item.title }}</div>
          <div class="card-meta">
            更新于 {{ formatDate(item.update_at || item.updatedAt) }}
          </div>
        </div>
        <div class="card-actions">
          <el-button size="small" type="primary" @click.stop="openScript(item)"
            >打开</el-button
          >
          <el-button size="small" @click.stop="renameScript(item)"
            >重命名</el-button
          >
          <el-button
            size="small"
            type="danger"
            text
            @click.stop="removeScript(item.id)"
            class="delete-btn"
          >
            <el-icon><Delete /></el-icon>
          </el-button>
        </div>
      </div>

      <div
        class="script-card create-card"
        role="button"
        tabindex="0"
        @click="dialogVisible = true"
        @keydown.enter="dialogVisible = true"
        @keydown.space.prevent="dialogVisible = true"
      >
        <div class="create-top">
          <div class="create-circle">
            <span class="create-symbol">+</span>
          </div>
          <div class="create-title">新建{{ labelProject }}作品</div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="null"
      width="640px"
      :close-on-click-modal="false"
      class="create-script-dialog"
      :show-close="false"
      :align-center="true"
      @close="resetForm"
    >
      <div class="dialog-header">
        <div class="header-content">
          <div class="header-icon">
            <el-icon><Edit /></el-icon>
          </div>
          <div class="header-text">
            <h2 class="dialog-title">创建{{ labelProject }}项目</h2>
            <p class="dialog-subtitle">{{ createProjectSubtitle }}</p>
          </div>
        </div>
      </div>

      <div class="dialog-body">
        <el-form
          ref="formRef"
          :model="form"
          :rules="formRules"
          label-position="top"
          class="create-form"
        >
          <div class="form-row">
            <el-form-item label="项目名称" prop="title" class="form-item-wide">
              <el-input
                v-model="form.title"
                :placeholder="'输入' + labelProject + '项目名称'"
                maxlength="50"
                show-word-limit
              />
            </el-form-item>
          </div>
        </el-form>
      </div>

      <div class="dialog-footer">
        <el-button
          @click="dialogVisible = false"
          size="large"
          class="cancel-btn"
          >取消创建</el-button
        >
        <el-button
          type="primary"
          @click="handleCreate"
          size="large"
          class="submit-btn"
        >
          开始创建
        </el-button>
      </div>
    </el-dialog>

    <!-- 修改提示词 -->
    <el-dialog v-model="dialogTableVisible" title="提示词">
      <el-table :data="gridData">
        <el-table-column property="name" label="提示词名字" />
        <el-table-column fixed="right" label="修改" min-width="120">
          <template #default="scope">
            <el-button
              link
              type="primary"
              size="small"
              @click.prevent="handleClick(scope.$index)"
            >
              查看提示词
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog v-model="dialogTableVisible2" :title="promptName">
      <el-button
        type="primary"
        class="modify-the-prompt"
        @click="ConfirmTheModificationPromptWords"
        >确认修改</el-button
      >
      <el-input
        v-loading="promptLoading"
        v-model="promptData"
        style="width: 100%"
        :autosize="{ minRows: 2, maxRows: 25 }"
        type="textarea"
        placeholder="Please input"
      />
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { Document, Edit, Delete } from "@element-plus/icons-vue";
import request from "@/api/request";
import {
  getSlowDramaWorkflowConfig,
  getSlowDramaWorkflowTypeFromRoute,
} from "@/config/slowDramaWorkflows";

export default {
  name: "SlowDramaView",
  components: { Document, Edit, Delete },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const dialogVisible = ref(false);
    const formRef = ref(null);
    const scripts = ref([]);
    const isLoading = ref(false);

    const workflowType = computed(() =>
      getSlowDramaWorkflowTypeFromRoute(route)
    );
    const workflowConfig = computed(() =>
      getSlowDramaWorkflowConfig(workflowType.value)
    );
    const labelProject = computed(() => workflowConfig.value.projectLabel);
    const detailRouteName = computed(
      () => workflowConfig.value.detailRouteName
    );
    const createProjectSubtitle = computed(() =>
      workflowType.value === "sweet_romance"
        ? "建立一个贴合女频细糠题材、关系驱动、高甜氛围的创作项目"
        : "建立一个偏长线叙事、节奏更从容的漫剧创作项目"
    );

    const form = ref({
      title: "",
    });

    const formRules = {
      title: [
        { required: true, message: "请输入项目名称", trigger: "blur" },
        { min: 1, max: 50, message: "名称长度为1-50个字符", trigger: "blur" },
      ],
    };

    const resetForm = () => {
      form.value = {
        title: "",
      };
      if (formRef.value) formRef.value.resetFields();
    };

    const fetchProjectsFromApi = async () => {
      isLoading.value = true;
      try {
        const res = await request.post("/slowDrama/search_for_project", {
          workflow_type: workflowType.value,
        });
        scripts.value = Array.isArray(res) ? res : [];
      } catch (error) {
        scripts.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const handleCreate = async () => {
      const valid = await formRef.value?.validate().catch(() => false);
      if (!valid) {
        ElMessage.warning("请完善表单信息");
        return;
      }

      const title = form.value.title.trim();
      try {
        const data = await request.post("/slowDrama/create_project", {
          title,
          workflow_type: workflowType.value,
        });
        if (data && data.id) {
          scripts.value.unshift(data);
          dialogVisible.value = false;
          resetForm();
          ElMessage.success(labelProject.value + "项目创建成功");
          router.push({
            name: detailRouteName.value,
            params: { id: data.id },
            query: { title: data.title || title },
          });
          return;
        }
        dialogVisible.value = false;
        resetForm();
        ElMessage.warning("创建成功，但未返回项目ID");
      } catch (error) {
        ElMessage.error("创建失败，请稍后重试");
      }
    };

    const openScript = (item) => {
      router.push({
        name: detailRouteName.value,
        params: { id: item.id },
        query: { title: item.title },
      });
    };

    const renameScript = async (item) => {
      try {
        const { value } = await ElMessageBox.prompt(
          "请输入新的项目名称",
          "重命名",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            inputValue: item.title,
          }
        );
        const name = (value || "").trim();
        if (!name) return;
        const data = await request.post("/slowDrama/rename_project", {
          project_id: item.id,
          title: name,
        });
        if (data && data.id) {
          item.title = data.title;
          item.update_at = data.update_at;
        }
        ElMessage.success("已重命名");
      } catch (error) {
        // 用户取消时不处理
      }
    };

    const removeScript = async (id) => {
      try {
        await ElMessageBox.confirm(
          "删除后将无法恢复，确定删除该" + labelProject.value + "项目吗？",
          "删除确认",
          {
            type: "warning",
            confirmButtonText: "确认删除",
            cancelButtonText: "取消",
          }
        );
        isLoading.value = true;
        const list = await request.post("/slowDrama/delete_project", {
          project_id: id,
        });
        scripts.value = Array.isArray(list)
          ? list
          : scripts.value.filter((item) => item.id !== id);
        ElMessage.success("已删除");
      } catch (error) {
        // 用户取消时不处理
      } finally {
        isLoading.value = false;
      }
    };

    const formatDate = (ts) => new Date(ts).toLocaleString();

    onMounted(async () => {
      await fetchProjectsFromApi();
    });

    watch(workflowType, () => {
      scripts.value = [];
      fetchProjectsFromApi();
    });

    const dialogTableVisible = ref(false);
    let dialogTableVisible2 = ref(false);
    let promptLoading = ref(false);
    const promptName = ref("");
    const promptData = ref("");
    const handleClick = async (e) => {
      console.log(gridData[e].name);
      const res = await request.post("/slowDrama/Get_prompts", {
        promptsType: gridData[e].name,
      });

      console.log(res);

      dialogTableVisible2.value = true;
      promptName.value = gridData[e].name;
      promptData.value = res.prompt;
    };

    const ConfirmTheModificationPromptWords = async () => {
      promptLoading.value = true; // 打开loading
      try {
        const res = await request.post("/slowDrama/Modify_prompt_words", {
          promptName: promptName.value,
          prompt: promptData.value,
        });
        console.log(res);
        if (res === 1) {
          ElMessage({ message: "修改成功", type: "success" });
        }
      } catch (err) {
        ElMessage({ message: "修改失败", type: "error" });
      } finally {
        promptLoading.value = false;
      }
    };

    const gridData = [
      {
        name: workflowConfig.value.synopsisAssistantTitle,
      },
      {
        name: workflowConfig.value.segmentAssistantTitle,
      },
      {
        name: workflowConfig.value.episodeOutlineTitle,
      },
      {
        name: workflowConfig.value.dialogueAssistantTitle,
      },
    ];

    return {
      dialogVisible,
      formRef,
      scripts,
      isLoading,
      form,
      formRules,
      labelProject,
      createProjectSubtitle,
      resetForm,
      handleCreate,
      openScript,
      renameScript,
      removeScript,
      formatDate,
      dialogTableVisible,
      dialogTableVisible2,
      gridData,
      handleClick,
      promptName,
      promptData,
      ConfirmTheModificationPromptWords,
      promptLoading,
    };
  },
};
</script>

<style scoped>
.slow-drama-view {
  padding: 20px 24px;
  width: 100%;
  height: 100%;
}

.scripts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.script-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
  cursor: default;
}

.script-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
}

.card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.card-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f0f4ff;
  color: #667eea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin-bottom: 12px;
}

.card-title {
  font-weight: 700;
  font-size: 18px;
  color: #222;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #8592a3;
}

.card-actions {
  display: flex;
  gap: 10px;
  padding: 12px 16px 16px;
  justify-content: center;
}

.card-actions .el-button {
  border-radius: 8px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
  padding: 8px 16px !important;
  font-size: 13px !important;
  min-width: 60px !important;
  height: 32px !important;
}

.card-actions .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  color: #ffffff !important;
}

.card-actions .delete-btn.el-button {
  background: #ffffff !important;
  border: 1px solid #ef4444 !important;
  color: #ef4444 !important;
  min-width: 32px !important;
  padding: 8px !important;
}

.create-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  cursor: pointer;
  outline: none;
}

.create-top {
  flex: 1;
  min-height: 200px;
  margin: 16px;
  border: 2px dashed var(--border-color, #cfd6e4);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    180deg,
    var(--background-secondary, #f7faff),
    var(--background-primary, #f9fbff)
  );
}

.create-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid #d9dff3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.create-symbol {
  font-size: 30px;
  line-height: 1;
  color: #667eea;
}

.create-title {
  font-size: 16px;
  color: #4f5d73;
  font-weight: 600;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.dialog-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.header-text {
  flex: 1;
}

.dialog-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: white;
}

.dialog-subtitle {
  font-size: 13px;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
}

.dialog-body {
  padding: 32px;
  background: #fff;
}

.create-form {
  max-width: 100%;
}

.form-row {
  margin-bottom: 24px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-item-wide :deep(.el-form-item__label),
.form-item-half :deep(.el-form-item__label) {
  font-weight: 600;
  color: #2d3748;
  font-size: 14px;
}

:deep(.el-input__wrapper) {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.dialog-footer {
  padding: 24px 32px 32px;
  background: #f8faff;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  border-top: 1px solid #e2e8f0;
}

.cancel-btn {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #6b7280;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
  color: #4b5563;
  transform: translateY(-1px);
}

.submit-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  padding: 12px 32px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  box-shadow: none;
  cursor: not-allowed;
}

.form-row-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.tags-box {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 10px;
}

.dynamic-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selectable-tag {
  cursor: pointer;
}

:deep(.create-script-dialog .el-dialog) {
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%);
}

:deep(.create-script-dialog .el-dialog__header) {
  display: none;
}

:deep(.create-script-dialog .el-dialog__body) {
  padding: 0;
  background: transparent;
}

:deep(.create-script-dialog .el-dialog__footer) {
  display: none;
}

@media (max-width: 768px) {
  .slow-drama-view {
    padding: 12px;
  }

  .form-row-split {
    grid-template-columns: 1fr;
  }
}

.modify-the-prompt {
  margin-bottom: 20px;
}
</style>
